import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from "react";
/**
 * 헌 위젯 지우기를 기다리는 한도.
 *
 * 토스가 응답하지 않으면 새 위젯을 영영 못 띄워 결제가 아예 막힌다. 그렇다고 그냥
 * 진행하면 "결제 UI 하나" 규칙을 어길 수 있다. 결제가 막히는 쪽이 더 나쁘므로
 * 1초까지만 기다리고 진행하되, 그런 일이 있었다는 것은 따로 보고한다.
 */ const DESTROY_TIMEOUT_MS = 1000;
/** 마운트마다 고유한 렌더 식별자를 만든다. mountKey는 금액이 경계를 왕복하면 재사용된다 */ let renderSequence = 0;
/** 이미 화면에서 사라진 자리를 지우려다 나는 오류. 정상 흐름이라 보고하지 않는다 */ const isBenignWidgetError = (error)=>{
    return typeof (error === null || error === void 0 ? void 0 : error.message) === "string" && error.message.includes("selector");
};
/**
 * 토스 결제위젯(결제수단 + 약관)을 실제로 띄우는 컴포넌트
 *
 * 위젯이 뜨고 사라지는 시점을 이 컴포넌트가 뜨고 사라지는 시점에 맞춘다.
 * 부모가 key를 바꿔 이 컴포넌트를 갈아끼우면 헌 위젯을 지우고 새로 띄우는 일이
 * 그냥 따라온다. "지금 다시 띄워야 하나"를 판단하는 코드가 아예 없다.
 *
 * 띄우는 순서는 토스가 정해둔 대로다 — 헌 위젯 지우기 → 금액 넣기 → 띄우기.
 * 한 페이지에 결제 UI는 하나만 있을 수 있어서 이 순서를 어기면 안 된다.
 * https://docs.tosspayments.com/sdk/v2/js
 *
 * 위젯이 붙을 자리(div)도 이 컴포넌트가 직접 그린다. 자리와 띄우는 코드가 서로 다른
 * 컴포넌트에 있으면 "자리가 아직 없는데 띄우려는" 순간이 생긴다.
 *
 * store·router·auth를 쓰지 않고 props만 받는다. 파일을 App Router로 옮겨도
 * 그대로 동작해야 하기 때문이다.
 */ const PaymentWidget = (param)=>{
    let { widgets , mountKey , variantKey , amount , pendingDestroyRef , onPaymentUiReady , onAmountApplied , onPaymentUiDestroyed , onError , onAmountSyncFailed  } = param;
    // useId 원본은 ':'를 포함해 CSS selector로 쓸 수 없다
    const uid = useId().replace(/:/g, "");
    const paymentMethodId = "payment-method-".concat(uid);
    const agreementId = "agreement-".concat(uid);
    const [paymentUi, setPaymentUi] = useState(null);
    // 위젯이 뜨는 도중에도 금액은 계속 바뀐다. 위젯을 다시 띄우지 않고 최신 금액을 읽으려고 ref에 둔다.
    // (예전에 옛 금액이 붙잡혀 위젯이 안 뜬 적이 있다)
    const amountRef = useRef(amount);
    amountRef.current = amount;
    // 부모가 매번 새 콜백을 넘겨도 위젯을 다시 띄우지 않도록 ref로 받는다
    const handlersRef = useRef({
        onPaymentUiReady,
        onAmountApplied,
        onPaymentUiDestroyed,
        onError,
        onAmountSyncFailed
    });
    handlersRef.current = {
        onPaymentUiReady,
        onAmountApplied,
        onPaymentUiDestroyed,
        onError,
        onAmountSyncFailed
    };
    useEffect(()=>{
        const renderId = "".concat(mountKey, "#").concat(renderSequence += 1);
        let cancelled = false;
        let rendered = [];
        const destroyRendered = ()=>{
            const targets = rendered;
            rendered = [];
            return Promise.all(targets.map((widget)=>widget.destroy().catch((error)=>{
                    if (isBenignWidgetError(error)) return;
                    handlersRef.current.onError("destroy", error);
                })));
        };
        /** 위젯 지우기를 기다리되, 1초를 넘기면 포기하고 진행한다 */ const destroyWithTimeout = ()=>new Promise((resolve)=>{
                let settled = false;
                const done = ()=>{
                    if (settled) return;
                    settled = true;
                    resolve();
                };
                const timer = setTimeout(()=>{
                    handlersRef.current.onError("destroy-timeout", new Error("결제위젯 destroy가 ".concat(DESTROY_TIMEOUT_MS, "ms 내에 완료되지 않았습니다")));
                    done();
                }, DESTROY_TIMEOUT_MS);
                destroyRendered().then(()=>{
                    clearTimeout(timer);
                    done();
                });
            });
        const render = async ()=>{
            // 헌 위젯이 완전히 지워진 다음에 띄운다 (토스 규칙)
            await pendingDestroyRef.current;
            if (cancelled) return;
            // 띄우기 전에 금액을 먼저 넣어야 한다 (토스 규칙)
            const renderAmount = amountRef.current;
            await widgets.setAmount({
                currency: "KRW",
                value: renderAmount
            });
            if (cancelled) return;
            const results = await Promise.allSettled([
                widgets.renderPaymentMethods({
                    selector: "#".concat(paymentMethodId),
                    variantKey
                }),
                widgets.renderAgreement({
                    selector: "#".concat(agreementId),
                    variantKey: "AGREEMENT"
                })
            ]);
            rendered = results.flatMap((result)=>result.status === "fulfilled" ? [
                    result.value
                ] : []);
            // 결제수단·약관 중 하나만 떴으면 뜬 쪽도 도로 지운다. 안 그러면 지울 수 없는 위젯이 남는다
            const rejected = results.find((result)=>result.status === "rejected");
            if (rejected) {
                await destroyRendered();
                throw rejected.reason;
            }
            if (cancelled) {
                await destroyRendered();
                return;
            }
            const nextPaymentUi = {
                widgets,
                renderId,
                variantKey,
                amount: renderAmount
            };
            setPaymentUi(nextPaymentUi);
            handlersRef.current.onPaymentUiReady(nextPaymentUi);
        };
        // 사라질 때 이 렌더가 끝나기를 기다릴 수 있도록 붙잡아 둔다
        const renderTask = render().catch((error)=>{
            if (cancelled) return;
            handlersRef.current.onError("render", error);
        });
        return ()=>{
            cancelled = true;
            // 결제 UI 해제는 반드시 여기, 언마운트와 같은 동기 구간에서 알린다.
            // 아래 destroy가 끝난 뒤로 미루면 그 사이 새 결제 UI가 이미 등록된 상태라,
            // 그것을 지워버려 결제 버튼이 잠긴 채로 남는다.
            setPaymentUi(null);
            handlersRef.current.onPaymentUiDestroyed(renderId);
            // 지우기 약속을 덮어쓰지 않고 뒤에 잇는다.
            // 다음 위젯이 뜨기 전에 끝나야 할 일이 셋이기 때문이다.
            //
            //  1) 그 전 위젯 지우기 — 아직 진행 중일 수 있다. 덮어쓰면 이걸 잃고,
            //     아직 안 지워진 위젯 위에 새 위젯이 뜬다
            //  2) 이 위젯의 렌더 마무리 — 뜨는 도중에 사라지면 위젯이 조금 뒤에 만들어진다.
            //     기다리지 않으면 그 위젯이 새 위젯과 함께 살아 있게 된다
            //  3) 이 위젯 지우기
            //
            // 셋 다 실패로 끝나지 않으므로 줄이 중간에 끊기지 않는다.
            const previous = pendingDestroyRef.current;
            pendingDestroyRef.current = previous.then(()=>renderTask).then(()=>destroyWithTimeout());
        };
    }, [
        widgets,
        variantKey,
        mountKey,
        paymentMethodId,
        agreementId,
        pendingDestroyRef
    ]);
    // 실시간 금액 갱신. 렌더가 끝난 결제 UI에만 보낸다.
    // (금액이 0이 되면 부모가 이 컴포넌트를 언마운트하므로 여기서 0을 걸러낼 필요가 없다)
    useEffect(()=>{
        if (!paymentUi || paymentUi.amount === amount) return;
        let cancelled = false;
        paymentUi.widgets.setAmount({
            currency: "KRW",
            value: amount
        }).then(()=>{
            if (cancelled) return;
            setPaymentUi((current)=>current ? {
                    ...current,
                    amount
                } : current);
            handlersRef.current.onAmountApplied(paymentUi.renderId, amount);
        }).catch((error)=>{
            if (cancelled) return;
            handlersRef.current.onError("set-amount", error);
            // 위젯이 든 금액을 믿을 수 없다. 부모가 위젯을 다시 띄워 복구한다
            handlersRef.current.onAmountSyncFailed();
        });
        // 늦게 끝난 금액 반영이 이미 갈린 위젯에 대고 보고하지 않도록 막는다.
        // 지우면 부모가 옛 금액을 최신인 줄 알게 된다.
        return ()=>{
            cancelled = true;
        };
    }, [
        paymentUi,
        amount
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                id: paymentMethodId
            }),
            /*#__PURE__*/ _jsx("div", {
                id: agreementId
            })
        ]
    });
};
export default PaymentWidget;
